import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { AppWindow, ChevronDown, Menu, X } from 'lucide-react';

const isItemActive = (item, activeTab) => {
  if (item.id === activeTab) return true;
  if (item.children) return item.children.some((c) => c.id === activeTab);
  return false;
};

const brand = 'var(--theme-primary)';
const tint = 'var(--theme-primary-soft)';
const tintSofter = 'var(--theme-primary-softer)';
const border = 'var(--theme-tertiary)';

const NavItem = ({ item, depth = 0, onToggle, activeTab, setActiveTab, onHoverEnter, onHoverLeave, expanded }) => {
  const Icon = item.icon;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const isParentActive = hasChildren && item.children.some((c) => c.id === activeTab);
  const isExactlyActive = !hasChildren && item.id === activeTab;
  const baseStyle = {
    color: isExactlyActive || isParentActive ? brand : '#64748b',
    background: isExactlyActive ? tint : 'transparent',
    fontWeight: isExactlyActive ? 700 : 500,
    paddingLeft: depth ? 36 : 20,
  };

  return (
    <>
      <button
        type="button"
        onMouseEnter={(e) => {
          if (onHoverEnter) onHoverEnter(item, e);
        }}
        onMouseLeave={() => {
          if (onHoverLeave) onHoverLeave();
        }}
        onClick={() => {
          if (hasChildren) onToggle(item.id);
          else setActiveTab(item.id);
        }}
        aria-current={isExactlyActive ? 'page' : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        className="w-full flex items-center text-left gap-3 pr-4 py-3 transition-all duration-200 relative group sidebar-nav-item"
        style={{ paddingLeft: 20 + depth * 16, ...baseStyle }}
      >
        {isExactlyActive && (
          <span
            className="absolute left-0 top-0 bottom-0 w-[3px]"
            style={{ background: brand }}
            aria-hidden="true"
          />
        )}
        {Icon && <Icon size={depth ? 14 : 17} aria-hidden="true" />}
        <span className={`flex-1 ${depth ? 'text-[11px]' : 'text-xs'} uppercase tracking-[0.12em]`}>{item.label}</span>
        {hasChildren && (
          <ChevronDown
            size={14}
            className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>
      {hasChildren && (
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="children"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              {item.children.map((child) => (
                <NavItem
                  key={child.id}
                  item={child}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  onHoverEnter={onHoverEnter}
                  onHoverLeave={onHoverLeave}
                  expanded={false}
                  onToggle={() => { }}
                  depth={depth + 1}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
};

NavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    children: PropTypes.array,
  }).isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  depth: PropTypes.number,
  setHoveredItem: PropTypes.func,
};

export const Sidebar = ({ org, items, activeTab, setActiveTab, onSwitchToFloating }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const initialExpanded = items.filter((i) => isItemActive(i, activeTab) && i.children).map((i) => i.id);
  const [expanded, setExpanded] = useState(initialExpanded);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleHoverEnter = (item, e) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredItem({ label: item.label, children: item.children, top: rect.top + rect.height / 2, left: rect.right + 8 });
  };

  const handleHoverLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, 150);
  };

  useEffect(() => {
    const parent = items.find((i) => i.children && i.children.some((c) => c.id === activeTab));
    if (parent && !expanded.includes(parent.id)) {
      setExpanded((prev) => [...prev, parent.id]);
    }
  }, [activeTab]);

  // Auto-close the drawer on selection.
  useEffect(() => { setMobileOpen(false); }, [activeTab]);

  const toggle = (id) => {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const panel = (drawer = false) => (
    <aside
      className={drawer
        ? 'flex flex-col h-full border-r'
        : 'hidden lg:flex flex-col flex-shrink-0 border-r'}
      style={{ width: drawer ? 'min(300px, 86vw)' : '260px', background: '#ffffff', borderColor: border }}
      aria-label="Primary navigation"
    >
      <div className="shrink-0 h-[52px] md:h-[56px] lg:h-[64px] px-6 flex items-center justify-between border-b" style={{ borderColor: border }}>
        <div className="flex items-center gap-2.5 min-w-0">
          <img
            src={org?.logo || "/logo.png"}
            alt={`${org?.name || 'Mitra POS'} Logo`}
            className="w-9 h-9 object-contain shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-extrabold uppercase tracking-[0.15em] truncate" style={{ color: brand }}>{org?.name || 'Mitra POS'}</p>
          </div>
        </div>
        {drawer && (
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
            className="lg:hidden p-1.5 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <nav className="flex-grow pt-1 pb-3 overflow-y-auto sidebar-scrollbar">
        {items.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            expanded={expanded.includes(item.id)}
            onToggle={toggle}
            onHoverEnter={handleHoverEnter}
            onHoverLeave={handleHoverLeave}
          />
        ))}
      </nav>

      <div className="py-3 border-t" style={{ borderColor: border }}>
        <button
          type="button"
          onMouseEnter={(e) => handleHoverEnter({ label: 'Floating' }, e)}
          onMouseLeave={handleHoverLeave}
          onClick={onSwitchToFloating}
          className="w-full flex items-center text-left gap-3 pr-4 py-3 transition-all duration-200 relative group sidebar-nav-item"
          style={{ color: '#64748b', fontWeight: 500, paddingLeft: 20 }}
        >
          <span
            className="absolute left-0 top-0 bottom-0 w-[3px] opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: brand }}
            aria-hidden="true"
          />
          <AppWindow size={17} aria-hidden="true" />
          <span className="flex-1 text-[11px] font-bold uppercase tracking-[0.12em]">
            Floating
          </span>
        </button>
        <div className="flex justify-center px-4 pb-1 pt-2">
          <p className="text-[10px] font-medium text-slate-400 tracking-wide text-center">
            &copy; {new Date().getFullYear()} Mitra POS. <span className="text-slate-400/70">All rights reserved.</span>
          </p>
        </div>
      </div>

      <AnimatePresence>
        {hoveredItem && (
          <motion.div
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={{ duration: 0.15 }}
            className="fixed z-[9999]"
            style={{ top: hoveredItem.top, left: hoveredItem.left, transform: 'translateY(-50%)' }}
            onMouseEnter={() => {
              if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
            }}
            onMouseLeave={handleHoverLeave}
          >
            <div className="relative px-3 py-2 bg-white text-[var(--theme-primary)] text-[10px] font-extrabold rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-[var(--theme-tertiary)] uppercase tracking-widest flex flex-col gap-1">
              <div className="absolute top-1/2 -left-[4.5px] -translate-y-1/2 w-2 h-2 bg-white border-b border-l border-[var(--theme-tertiary)] rotate-45" />
              <span className="relative z-10">{hoveredItem.label}</span>
              {hoveredItem.children && hoveredItem.children.length > 0 && (
                <div className="relative z-10 flex flex-col border-t border-[var(--theme-tertiary)] pt-1 mt-1 -mx-3">
                  {hoveredItem.children.map((child) => {
                    return (
                      <button 
                        key={child.id}
                        onClick={() => { setActiveTab(child.id); setHoveredItem(null); }}
                        className="w-full flex items-center text-left px-3 py-1.5 transition-all duration-200 relative group sidebar-nav-item overflow-hidden"
                        style={{ color: '#64748b' }}
                      >
                        <span
                          className="absolute left-0 top-0 bottom-0 w-[2.5px] opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: 'var(--theme-primary)' }}
                          aria-hidden="true"
                        />
                        <span className="flex-1 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap pl-1">
                          {child.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar */}
      {panel(false)}

      {/* Mobile trigger button — Floating action button */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation"
        className="lg:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform active:scale-95 hover:scale-105"
        style={{ background: brand, color: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
      >
        <Menu size={24} />
      </button>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="lg:hidden fixed inset-0 z-[120]"
              style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              key="drawer"
              initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden fixed inset-y-0 left-0 z-[130]"
            >
              {panel(true)}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        .sidebar-nav-item:hover { background: ${tintSofter}; color: ${brand}; }
      `}</style>
    </>
  );
};

Sidebar.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    children: PropTypes.array,
  })).isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  onSwitchToFloating: PropTypes.func.isRequired,
};
